import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ContactForm } from './ContactForm';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const LOGO_URL =
  '/api/static/brand/logo-h0.png';

const LANGS = [
  { code: 'ca', label: 'CA' },
  { code: 'es', label: 'ES' },
  { code: 'en', label: 'EN' },
  { code: 'fr', label: 'FR' },
];

const pickLang = (obj, lang) => (obj && (obj[lang] || obj.ca)) || '';

const LangSwitcher = () => {
  const { i18n } = useTranslation();
  return (
    <div className="fixed top-6 right-6 md:top-8 md:right-10 z-30 flex gap-3 md:gap-4 font-sans text-[11px] md:text-xs tracking-[0.25em]">
      {LANGS.map((l) => (
        <button
          key={l.code}
          type="button"
          onClick={() => i18n.changeLanguage(l.code)}
          className={`transition-colors duration-300 ${
            i18n.language === l.code ? 'text-[#D4AF37]' : 'text-[#A8B4A6] hover:text-[#F2E8D5]'
          }`}
        >
          {l.label}
        </button>
      ))}
    </div>
  );
};

const BackToH0 = () => (
  <Link
    to="/"
    data-testid="onia-back-h0"
    className="fixed top-6 left-6 md:top-8 md:left-10 z-30 group flex items-center gap-2 font-sans text-[10px] md:text-xs tracking-[0.32em] text-[#A8B4A6] hover:text-[#D4AF37] transition-colors uppercase"
  >
    <span className="inline-block transition-transform group-hover:-translate-x-1">←</span>
    H0
  </Link>
);

export const OnIaGolfPage = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language || 'ca';
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/api/on-ia-golf-content`)
      .then((r) => setContent(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading || !content) {
    return (
      <div className="min-h-screen w-full bg-[#0E4D2A] flex items-center justify-center text-[#A8B4A6] font-sans tracking-widest text-sm">
        ...
      </div>
    );
  }

  const visibleCommunities = (content.communities || []).filter((c) => c.active !== false).sort((a, b) => (a.order || 0) - (b.order || 0));
  const visibleApps = (content.apps || []).filter((a) => a.active !== false).sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div className="relative min-h-screen w-full bg-[#0E4D2A] text-[#F2E8D5] overflow-x-hidden" data-testid="on-ia-golf-page">
      {/* Watermark */}
      <div className="pointer-events-none fixed inset-0 z-0 flex items-center justify-center select-none">
        <span
          className="font-serif font-black text-[#083B1F]"
          style={{ fontSize: 'min(75vw, 75vh)', lineHeight: 1, opacity: 0.3 }}
          aria-hidden="true"
        >
          H0
        </span>
      </div>

      <BackToH0 />
      <LangSwitcher />

      <div className="relative z-10 max-w-5xl mx-auto px-5 md:px-10 pt-24 md:pt-32 pb-24">
        {/* HERO */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-col items-center text-center"
        >
          <img src={LOGO_URL} alt="Hoyo Zero" className="w-20 sm:w-24 h-auto" />
          <p className="mt-8 text-[#D4AF37] font-sans font-light tracking-[0.32em] uppercase text-[10px] sm:text-xs" data-testid="onia-eyebrow">
            {pickLang(content.eyebrow, lang)}
          </p>
          <h1 className="mt-4 font-serif text-4xl sm:text-5xl md:text-6xl leading-[1.05] tracking-tight max-w-3xl" data-testid="onia-hero-title">
            {pickLang(content.hero_title, lang)}
          </h1>
          <p className="mt-5 font-serif italic text-[#A8B4A6] text-lg md:text-xl max-w-2xl" data-testid="onia-hero-subtitle">
            {pickLang(content.hero_subtitle, lang)}
          </p>
        </motion.div>

        {/* WHAT IS H0 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.8 }}
          className="mt-24 md:mt-32 max-w-3xl mx-auto"
        >
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight" data-testid="onia-what-title">
            {pickLang(content.what_title, lang)}
          </h2>
          <p className="mt-6 font-sans font-light text-[#F2E8D5]/85 leading-relaxed text-base md:text-lg whitespace-pre-line" data-testid="onia-what-body">
            {pickLang(content.what_body, lang)}
          </p>
        </motion.div>

        {/* COMMUNITIES */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.8 }}
          className="mt-24 md:mt-32"
        >
          <div className="text-center">
            <p className="font-sans text-[10px] tracking-[0.32em] uppercase text-[#A8B4A6]">✦</p>
            <h2 className="mt-3 font-serif text-3xl md:text-4xl tracking-tight" data-testid="onia-communities-title">
              {pickLang(content.communities_title, lang)}
            </h2>
            <p className="mt-3 font-serif italic text-[#A8B4A6] text-base md:text-lg max-w-2xl mx-auto">
              {pickLang(content.communities_intro, lang)}
            </p>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-5 md:gap-6">
            {visibleCommunities.map((c, i) => (
              <motion.div
                key={c.key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="group relative rounded-2xl border border-[#D4AF37]/20 hover:border-[#D4AF37]/50 bg-[#083B1F]/40 backdrop-blur-sm p-6 md:p-7 transition-all duration-500"
                data-testid={`onia-community-${c.key}`}
              >
                <div className="text-4xl mb-4">{c.emoji}</div>
                <h3 className="font-serif text-2xl tracking-tight">{c.name}</h3>
                <p className="mt-3 font-sans font-light text-[#F2E8D5]/80 leading-relaxed text-sm md:text-base">
                  {pickLang(c.description, lang)}
                </p>
                <Link
                  to={c.url}
                  className="mt-6 inline-flex items-center gap-2 font-sans text-[11px] tracking-[0.28em] uppercase text-[#D4AF37] group-hover:gap-3 transition-all"
                  data-testid={`onia-community-cta-${c.key}`}
                >
                  {pickLang(c.cta, lang)}
                  <span>→</span>
                </Link>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* APPS */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.8 }}
          className="mt-24 md:mt-32"
        >
          <div className="text-center">
            <p className="font-sans text-[10px] tracking-[0.32em] uppercase text-[#A8B4A6]">✦</p>
            <h2 className="mt-3 font-serif text-3xl md:text-4xl tracking-tight" data-testid="onia-apps-title">
              {pickLang(content.apps_title, lang)}
            </h2>
            <p className="mt-3 font-serif italic text-[#A8B4A6] text-base md:text-lg max-w-2xl mx-auto">
              {pickLang(content.apps_intro, lang)}
            </p>
          </div>
          <div className="mt-12 grid md:grid-cols-2 gap-4 md:gap-6">
            {visibleApps.map((app, i) => (
              <motion.a
                key={app.key}
                href={app.url}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className="group block rounded-2xl border border-[#D4AF37]/15 hover:border-[#D4AF37]/40 bg-[#083B1F]/30 p-6 transition-all duration-500"
                data-testid={`onia-app-${app.key}`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-3xl">{app.emoji}</span>
                  <h3 className="font-serif text-xl md:text-2xl tracking-tight">{app.title}</h3>
                </div>
                <p className="mt-3 font-sans font-light text-[#F2E8D5]/80 leading-relaxed text-sm md:text-base">
                  {pickLang(app.description, lang)}
                </p>
                <span className="mt-4 inline-flex items-center gap-2 font-sans text-[10px] tracking-[0.28em] uppercase text-[#D4AF37] group-hover:gap-3 transition-all">
                  {lang === 'es' ? 'Visitar' : lang === 'en' ? 'Visit' : lang === 'fr' ? 'Visiter' : 'Visitar'}
                  <span>↗</span>
                </span>
              </motion.a>
            ))}
          </div>
        </motion.div>

        {/* Footer */}
        <p className="mt-24 text-center text-[10px] tracking-[0.3em] uppercase text-[#A8B4A6]/60 font-sans">
          © {new Date().getFullYear()} · Hoyo Zero · @joan_monzo
        </p>
      </div>

      {/* Contact section — "En què et puc ajudar?" */}
      <div className="relative z-10" data-testid="onia-contact-section">
        <ContactForm />
      </div>
    </div>
  );
};

export default OnIaGolfPage;
